import { Component, OnInit } from '@angular/core';
import { COMMON_IMPORTS, MATERIAL_IMPORTS } from '../../imports/common-imports';
import { MatDialogRef } from '@angular/material/dialog';
import { LambdaApiService } from '../../services/lambda-api.service';
import { FilterStateService } from '../../services/filter-state.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { Region } from '../../models/region.model';
import { Market } from '../../models/market.model';

@Component({
  selector: 'app-market-region-filter',
  standalone: true,
  imports: [COMMON_IMPORTS, MATERIAL_IMPORTS, SpinnerComponent],
  templateUrl: './market-region-filter.component.html',
  styleUrl: './market-region-filter.component.scss',
})
export class MarketRegionFilterComponent implements OnInit {
  typeSelected: 'region' | 'market' = 'region';
  regions: { id: string; name: string; isSelected: boolean }[] = [];
  markets: (Market & { checked: boolean })[] = [];
  isLoading: boolean = false;

  constructor(
    private readonly dialogRef: MatDialogRef<MarketRegionFilterComponent>,
    private readonly apiService: LambdaApiService,
    private readonly filterStateService: FilterStateService,
  ) {}

  ngOnInit() {
    // If there are active market filters, start with market radio selected
    if (this.filterStateService.filterData.markets.length > 0) {
      this.typeSelected = 'market';
    }
    this.loadRegions();
  }

  loadRegions() {
    this.isLoading = true;
    const userAvailableRegions =
      this.filterStateService.pristineFilters.regions;
    const appliedRegions = this.filterStateService.filterData.regions;
    const hasAppliedRegionFilter =
      this.filterStateService.isFilterApplied('regions');

    this.apiService
      .getRegions()
      .then((data: Region[]) => {
        data = data.filter((region) =>
          userAvailableRegions.includes(region.id),
        );

        this.regions = data.map((region: Region) => ({
          ...region,
          // If there's an applied region filter, check based on applied filters
          // Otherwise, start unchecked
          isSelected: hasAppliedRegionFilter
            ? appliedRegions.includes(region.id)
            : false,
        }));
        this.loadMarkets();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  loadMarkets() {
    this.isLoading = true;
    const availableMarkets = new Set(
      this.filterStateService.pristineFilters.markets,
    );
    const appliedMarkets = this.filterStateService.filterData.markets;
    const hasAppliedMarketFilter =
      this.filterStateService.isFilterApplied('markets');

    this.apiService
      .getMarkets()
      .then((data: Market[]) => {
        // Filter markets if pristineFilters.markets is not _ALL
        if (!availableMarkets.has('_ALL')) {
          data = data.filter((market) => availableMarkets.has(market.name));
        }
        this.markets = data.map((market: Market) => ({
          ...market,
          // If there's an applied market filter, check based on applied filters
          // Otherwise, start unchecked
          checked: hasAppliedMarketFilter
            ? appliedMarkets.includes(market.name)
            : false,
        }));

        // order markets by name
        this.markets.sort((a, b) => a.name.localeCompare(b.name));
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  getSelectedRegions(): { id: string; name: string; isSelected: boolean }[] {
    return this.regions.filter((region) => region.isSelected);
  }

  getSelectedMarkets(): (Market & { checked: boolean })[] {
    return this.markets.filter((market) => market.checked);
  }

  isEveryRegionSelected(): boolean {
    return this.regions
      .filter((region) => region.name)
      .every((region) => region.isSelected);
  }

  toggleAllRegions() {
    const allRegionsSelected = this.isEveryRegionSelected();
    this.regions.forEach((region) => {
      region.isSelected = !allRegionsSelected;
    });
  }

  isEveryMarketInRegionSelected(regionId: string): boolean {
    const marketsInRegion = this.markets.filter(
      (market) => market.region === regionId,
    );
    return (
      marketsInRegion.length > 0 &&
      marketsInRegion.every((market) => market.checked)
    );
  }

  toggleAllMarketsInRegion(regionId: string): void {
    const allSelected = this.isEveryMarketInRegionSelected(regionId);
    this.markets.forEach((market) => {
      if (market.region === regionId) {
        market.checked = !allSelected;
      }
    });
  }

  isApplyDisabled(): boolean {
    if (this.typeSelected === 'region') {
      return this.getSelectedRegions().length === 0;
    }
    return this.getSelectedMarkets().length === 0;
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    if (this.typeSelected === 'region') {
      // Filter by regions - store selected region IDs
      const selectedRegions = this.getSelectedRegions().map(
        (region) => region.id,
      );
      this.filterStateService.filterData.regions = [...selectedRegions];
      // Clear market filters when filtering by region
      this.filterStateService.filterData.markets = [];
      // Track applied filter key
      this.filterStateService.applyFilter('regions');
      this.filterStateService.clearFilter('markets');
    } else {
      // Filter by markets - store selected market names
      const selectedMarkets = this.getSelectedMarkets().map(
        (market) => market.name,
      );
      this.filterStateService.filterData.markets = [...selectedMarkets];
      // Clear region filters when filtering by market
      this.filterStateService.filterData.regions = [];
      // Track applied filter key
      this.filterStateService.applyFilter('markets');
      this.filterStateService.clearFilter('regions');
    }
    this.dialogRef.close({ refresh: true });
  }
}
